import { useState } from "react";
import { Email, Visibility, VisibilityOff } from "@mui/icons-material";
import Papa from "papaparse";
import csvFile from "../../assets/list.csv";
import { toast } from "react-toastify";
import "./auth.scss";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/png/auth-logo.png";
import UGXlogo from "../../assets/png/logo-min.png";
import LockIcon from "@mui/icons-material/Lock";

const AuthenticationPage = () => {
  const [csvError, setCsvError] = useState(false);
  const [data, setData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_DGPV_API_URL;

  const onChange = (e) => {
    if (!e.target) return;
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      toast.error("Session expired. Please login again.");
      navigate("/login");
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/token/refresh/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Failed to refresh token");
      }

      localStorage.setItem("authToken", result.access);
      return result.access;
    } catch (error) {
      console.error("Token refresh error:", error);
      toast.error("Session expired. Please login again.");
      navigate("/login");
      return null;
    }
  };

  const authFetch = async (url, options = {}) => {
    let accessToken = localStorage.getItem("authToken");

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.status === 401) {
      const newAccessToken = await refreshAccessToken();
      if (!newAccessToken) return response;

      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newAccessToken}`,
        },
      });
    }

    return response;
  };

  const handleLogin = async (loginData) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Login failed");
      }

      localStorage.setItem("authToken", result.access);
      localStorage.setItem("refreshToken", result.refresh);
      localStorage.setItem("permissionLevel", result.user.permission_level);
      localStorage.setItem("username", result.user.username);

      toast.success("Login successful!");
      navigate("/asset-master", { replace: true });
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "An error occurred during login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuth = (e) => {
    e.preventDefault();
    handleLogin({
      email: data.email,
      password: data.password,
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="auth-card-container">
      <img src={UGXlogo} alt="Auth Logo" className="auth-logo" />
      <h2 className="auth-subtitle">ASSET MANAGER</h2>
      <div className="auth-card">
        {/* Header */}
        <div className="auth-header">
          <h1>Sign In</h1>
          <p>Sign in to access your dashboard</p>
        </div>

        <form className="auth-form" onSubmit={handleAuth}>
          {/* Email */}
          <div className="form-group">
            <label>
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              value={data.email}
              onChange={onChange}
              autoComplete="email"
            />
          </div>

          {/* Password */}
          <div className="form-group">
            <label>
              Password <span className="required">*</span>
            </label>

            <div className="password-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="********"
                value={data.password}
                onChange={onChange}
                autoComplete="current-password"
              />

              <span onClick={togglePasswordVisibility}>
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </span>
            </div>
          </div>

          {/* Button */}
          <button
            type="submit"
            className="auth-button"
            disabled={csvError || isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AuthenticationPage;
